"use client";

import { useEffect, useRef } from "react";
import { X } from "lucide-react";

const FOCUSABLE_SELECTOR =
  "a[href], button:not([disabled]), input:not([disabled]):not([type='hidden']), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])";

export default function AuthDialogShell({ children, onClose, label = "Authentication" }) {
  const contentRef = useRef(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const previouslyFocused = document.activeElement;
    const focusable = contentRef.current?.querySelector(
      "input, button, select, textarea, [tabindex]:not([tabindex='-1'])"
    );
    focusable?.focus();

    // Same keyboard contract as BaseModal (customer login): Escape closes, Tab stays inside.
    const handleKeyDown = (event) => {
      const dialog = contentRef.current;
      if (!dialog) return;
      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current?.();
        return;
      }
      if (event.key !== "Tab") return;
      const focusableElements = Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
        (element) => !element.closest("[hidden]") && element.getAttribute("aria-hidden") !== "true"
      );
      if (!focusableElements.length) return;
      const first = focusableElements[0];
      const last = focusableElements[focusableElements.length - 1];
      const outside = !dialog.contains(document.activeElement);
      if (event.shiftKey && (outside || document.activeElement === first)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (outside || document.activeElement === last)) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      previouslyFocused?.focus?.();
    };
  }, []);

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-transparent px-4 py-8 [@media(max-height:480px)]:py-3"
      data-allow-overlay="1"
      role="presentation"
      onClick={() => onClose?.()}
    >
      <div className="absolute inset-0 bg-black/60" />
      <div
        ref={contentRef}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        // Bounded by the overlay's padded height so short landscape screens scroll the dialog
        // instead of clipping its top and bottom; items-start keeps the card at its natural height.
        className="relative z-10 flex max-h-full w-full max-w-lg items-start justify-center overflow-y-auto overscroll-contain"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          aria-label="Close"
          onClick={() => onClose?.()}
          className="absolute right-3 top-3 z-20 flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white/95 text-slate-900 shadow-sm transition hover:bg-white"
        >
          <X className="h-5 w-5" />
        </button>
        {children}
      </div>
    </div>
  );
}
